(function (scope) {
  'use strict';
  const text = value => String(value ?? '').trim();
  const normalizeName = value => text(value).toLowerCase().replace(/[_-]/g, ' ');
  const color = value => {
    const v = text(value);
    if (/^(white|white gold|白|白色|白金色)$/i.test(v)) return 'White';
    if (/^(yellow|yellow gold|黄金|黄色|金色)$/i.test(v)) return 'Yellow';
    if (/^(rose|rose gold|pink gold|玫瑰金|玫瑰金色)$/i.test(v)) return 'Rose';
    return null;
  };
  function metalColor(value) {
    const match = text(value).match(/^(\d+\s*k(?:t)?|platinum|silver|sterling silver)\s*(?:gold\s*)?(?:\/|\+|\|)?\s*(white|yellow|rose|pink|白色|黄色|玫瑰金)(?:\s*gold)?$/i);
    if (!match) return null;
    return [match[1], color(match[2]) || match[2]];
  }
  function classify(name) {
    const n = normalizeName(name);
    if (/weight|carat|ctw|克拉|重量/.test(n) && /quality|clarity|品质|净度/.test(n)) return ['weight', 'quality'];
    if (/metal|material|gold type|金属|材质|金的类型/.test(n) && /colou?r|颜色/.test(n)) return ['metal', 'color'];
    if (/weight|carat|ctw|克拉|重量/.test(n)) return ['weight'];
    if (/metal|material|gold type|金属|材质|金的类型/.test(n)) return ['metal'];
    if (/colou?r|颜色/.test(n)) return ['color'];
    if (/quality|clarity|品质|净度/.test(n)) return ['quality'];
    if (/size|length|尺寸|长度|圈号|戒围|链长/.test(n)) return ['size'];
    return [];
  }
  function normalized(value, key) {
    const v = text(value);
    if (key === 'color') return color(v) || v;
    if (key === 'metal') {
      const m = v.match(/^(\d+)\s*k(?:t)?(?:\s*gold)?$/i);
      if (m) return m[1] + 'KT';
      if (/^platinum$/i.test(v)) return 'Platinum';
    }
    if (key === 'weight') {
      const m = v.match(/^(\d+(?:\.\d+)?)\s*(ct|ctw|cttw|carats?)$/i);
      if (m) return Number(m[1]) + ' ' + (/ctw|cttw/i.test(m[2]) ? m[2].toUpperCase() : 'ct');
    }
    return v;
  }
  function build(product, category) {
    const variants = Array.isArray(product.variants) ? product.variants : product.variants?.nodes || [];
    const options = (product.options || []).filter(o => !(o.name === 'Title' && o.values?.length === 1 && o.values[0] === 'Default Title'));
    const labels = {weight: 'Diamond Weight', metal: 'Metal', color: 'Color', quality: 'Diamond Quality', size: category === 'rings' ? 'Ring Size' : category === 'necklaces' ? 'Chain Length' : 'Size'};
    const plans = options.map((option, index) => {
      const values = [...new Set([...(option.values || []).map(text), ...variants.map(v => text((v.selectedOptions || []).find(o => o.name === option.name)?.value))])].filter(Boolean);
      let keys = classify(option.name);
      let split = value => [value];
      if (keys.length === 2) {
        split = value => {
          if (keys[0] === 'metal') return metalColor(value);
          const m = text(value).match(/^(.+?)\s*(?:\/|\|)\s*(.+)$/);
          return m && [m[1], m[2]];
        };
        if (!values.every(v => split(v)?.every(text))) keys = [];
      } else if (keys[0] === 'metal' && values.length && values.every(v => metalColor(v))) {
        keys = ['metal', 'color']; split = metalColor;
      } else if (/^configuration$/i.test(option.name) && values.length && values.every(v => /\s\/\s*(White|Yellow|Rose)$/i.test(v))) {
        keys = ['configuration', 'color'];
        split = value => { const m = text(value).match(/^(.*)\s\/\s*(White|Yellow|Rose)$/i); return m && [m[1], m[2]]; };
      }
      if (!keys.length) { keys = ['raw:' + index]; split = value => [value]; }
      return {option, index, keys, split};
    });
    const used = new Map();
    plans.forEach(p => p.keys.forEach(k => used.set(k, (used.get(k) || 0) + 1)));
    // If two backend options have the same meaning, preserve both raw fields.
    plans.forEach(p => { if (p.keys.some(k => used.get(k) > 1)) { p.keys = ['raw:' + p.index]; p.split = value => [value]; } });
    const rows = variants.flatMap(variant => {
      const selection = {};
      for (const p of plans) {
        const option = (variant.selectedOptions || []).find(o => o.name === p.option.name);
        const values = option && p.split(text(option.value));
        if (!values || values.length !== p.keys.length || values.some(v => !text(v))) return [];
        p.keys.forEach((k, i) => { selection[k] = normalized(values[i], k); });
      }
      return [{variant, selection}];
    });
    const groups = plans.flatMap(p => p.keys.map(key => ({
      key, label: labels[key] || p.option.name,
      values: [...new Set(rows.map(r => r.selection[key]))]
    }))).filter(g => g.values.length);
    const order = ['weight', 'metal', 'color', 'quality', 'size'];
    groups.sort((a, b) => (order.includes(a.key) ? order.indexOf(a.key) : 10) - (order.includes(b.key) ? order.indexOf(b.key) : 10));
    return {groups, rows, category};
  }
  function resolve(model, selection, product) {
    const matches = model.rows.filter(row => model.groups.every(group => row.selection[group.key] === selection[group.key]));
    const variant = matches.length === 1 ? matches[0].variant : null;
    const amount = variant ? Number(variant.priceMinor) : NaN;
    const quoted = !!variant && Number.isSafeInteger(amount) && amount > 0;
    const incomplete = Object.values(selection).some(v => /待确认|to be confirmed|unknown/i.test(v));
    return {variant, quoted, amount, ambiguous: matches.length > 1,
      purchasable: quoted && !incomplete && product.purchaseEnabled === true && variant.available === true && /^\d+$/.test(String(variant.id))};
  }
  const api = {build, resolve, classify};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else scope.VRJewelryOptions = api;
})(typeof window !== 'undefined' ? window : globalThis);
